import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { Box, Spinner, Text } from "@chakra-ui/react";
import { AutoSaveProvider, AutoSaveIndicator } from "newui/components/ui/AutoSave";
import { UploadedFile } from "newui/components/FileUpload";
import { RfxDocumentRow } from "./types/rfx-document.types";
import { useRfxDocuments } from "./hooks/useRfxDocuments";
import { useRfxDocumentMutations } from "./hooks/useRfxDocumentMutations";
import { useDocumentFilters } from "./hooks/useDocumentFilters";
import { useDocumentSelection } from "./hooks/useDocumentSelection";
import { useDuplicateDetection } from "./hooks/useDuplicateDetection";
import { useRfqDocumentCategories } from "newui/components/ui/CommonSelects";
import { useRfxData } from "../data-provider/RfxDataProvider";
import { DocumentUploadSection } from "./components/DocumentUploadSection";
import { DocumentTable } from "./components/DocumentTable";
import { DocumentBulkActionsBar } from "./components/DocumentBulkActionsBar";
import { DocumentDeleteModal } from "./components/DocumentDeleteModal";
import { BatchResolution } from "./components/BatchDuplicateResolutionModal";
import { SingleDuplicateResolution } from "./components/DuplicateFilenameModal";

interface RfxDocumentManagerProps {
  /**
   * Hide the Revision column. Defaults to true in the creation flow (set by
   * the caller) since every newly-uploaded document is implicitly the first
   * revision and showing the column there is noise.
   */
  hideRevision?: boolean;
  /**
   * True when this manager is used inside the RFx Template editor.
   * Routes uploads to the template-aware FileUploadController handler so
   * the backend resolves an `rfx_template_id` instead of an `rfq_id`.
   * Defaults to false (real RFx context).
   */
  isTemplate?: boolean;
  rfqId: number;
}

// `useRfxDocuments` is a plain useQuery with no initialData, so `data` is undefined
// while the query is in flight. Defaulting inline (`= []`) would build a NEW array on
// every render, which changes the `filteredDocuments` memo's identity every render,
// which re-fires useDocumentSelection's selection-clearing effect, which sets state and
// re-renders -- an infinite loop for as long as the query is loading. A module-level
// constant keeps the empty case referentially stable and breaks that cycle.
const NO_DOCUMENTS: RfxDocumentRow[] = [];

export const RfxDocumentManager = ({ rfqId, isTemplate = false, hideRevision = false }: RfxDocumentManagerProps) => {
  const { data: documents = NO_DOCUMENTS, isLoading, isFetching, refetch } = useRfxDocuments(rfqId, { isTemplate });
  // The RFx Preview consumes documents from the broader RfxDataProvider, which
  // loads the RFx once and caches it. Without poking the provider it never
  // sees document changes (reorder / category / new uploads) in real time.
  // `useRfxData(true)` returns null when there's no provider in the tree
  // (e.g. the template editor) so we can call `reloadRfxData` defensively.
  const rfxContext = useRfxData(true);
  // True while at least one file is mid-upload. Set by DocumentUploadSection
  // so we can hold the table in a loading state until everything has landed
  // AND the table has refetched.
  const [isUploading, setIsUploading] = useState(false);
  const mutations = useRfxDocumentMutations(rfqId, { isTemplate });
  const { filters, setFilter, filterDocuments, clearFilters, hasActiveFilters } = useDocumentFilters();
  const { options: categoryOptions } = useRfqDocumentCategories();
  const duplicateDetection = useDuplicateDetection(rfqId, { isTemplate });

  const filteredDocuments = useMemo(
    () => filterDocuments(documents),
    [filterDocuments, documents]
  );

  const selection = useDocumentSelection(filteredDocuments);

  // The RFx Preview (CreateRFQ flow) is rendered from Formik form values via
  // `convertDraftRfxDataToRfx`, NOT from the documents react-query cache. So
  // whenever the backend state of documents changes (reorder, upload,
  // category edit, delete) we must mirror it back into Formik's `documents`
  // field so the preview reflects reality in real time.
  // `useFormikContext` returns an object even outside a Formik provider —
  // the `setFieldValue` will be undefined in that case (template editor), so
  // we guard before calling it.
  const formik = useFormikContext<any>();
  // Stash setFieldValue in a ref so the effect can call it without depending
  // on the formik object (whose identity churns every render and would
  // re-fire this sync constantly).
  const setFieldValueRef = useRef(formik?.setFieldValue);
  setFieldValueRef.current = formik?.setFieldValue;
  useEffect(() => {
    if (!setFieldValueRef.current) return;
    const mapped = documents.map((d) => ({
      id: d.id,
      document: d.fileName,
      category_id: d.categoryId,
      sort_order: d.sortOrder,
      file_size: d.fileSize,
      path: d.documentPath,
      isRemove: 0,
    }));
    setFieldValueRef.current("documents", mapped, false);
  }, [documents]);

  // Delete modal state
  const [deleteTargets, setDeleteTargets] = useState<RfxDocumentRow[]>([]);

  const handleDeleteRequest = (doc: RfxDocumentRow) => {
    setDeleteTargets([doc]);
  };

  const handleBulkDeleteRequest = () => {
    const targets = documents.filter((d) => selection.selectedIds.has(d.id));
    setDeleteTargets(targets);
  };

  const handleDeleteConfirm = async () => {
    const ids = deleteTargets.map((d) => d.id);
    await mutations.bulkDeleteMutation.mutateAsync(ids);
    selection.clearSelection();
    setDeleteTargets([]);
    rfxContext?.reloadRfxData();
  };

  const handleBulkChangeCategory = async (categoryId: number) => {
    await mutations.bulkChangeCategoryMutation.mutateAsync({
      docIds: Array.from(selection.selectedIds),
      categoryId,
    });
    selection.clearSelection();
    rfxContext?.reloadRfxData();
  };

  // Any document mutation may affect what the RFx Preview shows
  // (documentationDocument is part of the cached RFx blob loaded once by the
  // provider). Poke the provider to refetch so the preview stays in sync in
  // real time — no-op when there's no provider in the tree (e.g. template
  // editor).
  const reloadRfxContext = () => rfxContext?.reloadRfxData();

  const handleUpdate = async (
    docId: number,
    data: { category_id?: number; external_revision?: string }
  ) => {
    await mutations.updateMutation.mutateAsync({ docId, data });
    reloadRfxContext();
  };

  const handleReorder = (orderedIds: number[]) => {
    mutations.reorderMutation.mutate(orderedIds, {
      onSettled: reloadRfxContext,
    });
  };

  const handleFilesSelected = async (files: File[]): Promise<boolean> => {
    return duplicateDetection.checkFiles(files);
  };

  const handleUploadComplete = (_files: UploadedFile[]) => {
    refetch();
    rfxContext?.reloadRfxData();
  };

  const handleDuplicateResolved = (
    _resolutions: SingleDuplicateResolution | BatchResolution[]
  ) => {
    duplicateDetection.clearConflicts();
    refetch();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <AutoSaveProvider>
      <Box data-testid="rfx-document-manager">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold">
            Documents
          </Text>
          <AutoSaveIndicator />
        </Box>

        <DocumentUploadSection
          rfqId={rfqId}
          isTemplate={isTemplate}
          categoryOptions={categoryOptions}
          hasDuplicates={duplicateDetection.hasConflicts}
          duplicates={duplicateDetection.conflicts}
          isCheckingDuplicates={duplicateDetection.isChecking}
          onFilesSelected={handleFilesSelected}
          onUploadComplete={handleUploadComplete}
          onDuplicateResolved={handleDuplicateResolved}
          onDuplicateModalClose={duplicateDetection.clearConflicts}
          onBulkReplaceDelete={async (ids) => {
            await mutations.bulkDeleteMutation.mutateAsync(ids);
          }}
          onUploadingChange={setIsUploading}
        />

        <Box mt={6}>
          <DocumentTable
            hideRevision={hideRevision}
            isBusy={isUploading || isFetching}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            documents={documents}
            filteredDocuments={filteredDocuments}
            filters={filters}
            categoryOptions={categoryOptions}
            selectedIds={selection.selectedIds}
            isAllSelected={selection.isAllSelected}
            isIndeterminate={selection.isIndeterminate}
            onFilterChange={setFilter}
            onToggleSelect={selection.toggleSelection}
            onSelectAll={selection.selectAll}
            onClearSelection={selection.clearSelection}
            onUpdate={handleUpdate}
            onDeleteRequest={handleDeleteRequest}
            onReorder={handleReorder}
          />
        </Box>

        <DocumentBulkActionsBar
          selectedCount={selection.selectedIds.size}
          categoryOptions={categoryOptions}
          onBulkDelete={handleBulkDeleteRequest}
          onBulkChangeCategory={handleBulkChangeCategory}
        />

        <DocumentDeleteModal
          isOpen={deleteTargets.length > 0}
          documents={deleteTargets}
          isDeleting={mutations.bulkDeleteMutation.isPending}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTargets([])}
        />
      </Box>
    </AutoSaveProvider>
  );
};
